/**
 * ============================================================================
 *  File        : frontend/src/api/settlements.ts
 *  Module      : api
 *  Purpose     : 精算 API — 一回目（P1）試算 + N34 手入力（manual amount）
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 *
 *  後端 handler：crates/api-http v13_settlement_runs。
 *  一回目（P1）：
 *  - POST /api/v1/cases/:id/settlements/p1/calculate → P1ResultDto
 *  - POST /api/v1/settlements/p1/runs/:run_id/manual-amount → ManualAmountResultDto
 *  二回目（P2，三方分配 P+Q+R=K + ⑤sheet T33/T37/T38）：
 *  - POST /api/v1/cases/:id/settlements/p2/calculate → P2ResultDto（每次新 run，non-overwrite）
 *  - GET  /api/v1/cases/:id/settlements/p2/latest    → SecondSettlementRunDto | null
 *  - POST /api/v1/settlements/p2/runs/:run_id/manual-amount → ManualAmountResultDto（T38 EF 實際請求）
 *  ⚠️ 金額・重量一律字串型 Decimal，前端不得轉 float（CLAUDE.md §2）。
 *  package_type 合法值：flexible / small_flexible / bara / pallet / other。
 */

import { API_BASE, mutationHeaders, requestData, type ApiSchemas } from './client'

/** P1 試算單列輸入（對齊後端 CalculateItemDto）。 */
export interface CalculateItemDto {
  item_management_no: string
  gross_weight_with_bag_kg: string
  min_unit_price: string
  bag_count: number
  package_type: string
  bag_unit_weight_kg: string
}

/** P1 試算 request body（對齊後端 CalculateFirstSettlementDto）。 */
export interface CalculateFirstSettlementDto {
  case_no: string
  items: CalculateItemDto[]
}

/** P1 試算結果（對齊後端 P1ResultDto）。 */
export interface P1ResultDto {
  run_id: number
  settlement_phase: string
  /** L sum 税別（SUM(E×K)）。 */
  l_sum_excl_tax: string
  /** L34 一回目試算總額（税込）= SUM(E×K)×1.1。 */
  l34_total_incl_tax: string
  /** M34 50% 參考前受金。 */
  m34_reference_50pct: string
}

/** N34 手入力 request body（對齊後端 ManualAmountDto）。 */
export interface ManualAmountDto {
  amount: string
}

/** N34 手入力結果（對齊後端 ManualAmountResultDto）。 */
export interface ManualAmountResultDto {
  run_id: number
  status: string
}

/** 一回目（P1）試算：每次呼叫新增一個 settlement_run（non-overwrite）。 */
export function calculateP1(
  caseId: number | string,
  body: CalculateFirstSettlementDto,
): Promise<P1ResultDto> {
  return requestData<P1ResultDto>(`${API_BASE}/cases/${caseId}/settlements/p1/calculate`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

/**
 * N34 EF 實際請求金額手入力（對特定 run_id）。
 * 高風險端點（spec §9.2）:需帶 2FA step-up `code`（→ `X-2FA-Code` header）。
 */
export function submitP1ManualAmount(
  runId: number,
  body: ManualAmountDto,
  stepUpCode?: string,
): Promise<ManualAmountResultDto> {
  return requestData<ManualAmountResultDto>(
    `${API_BASE}/settlements/p1/runs/${runId}/manual-amount`,
    { method: 'POST', body, headers: mutationHeaders(stepUpCode) },
  )
}

// ---- 二回目（P2）三方分配 + ⑤sheet ----------------------------------------

/** P2 試算單列輸入（對齊後端 CalculateSecondSettlementItemDto）。 */
export type CalculateSecondSettlementItemDto = ApiSchemas['CalculateSecondSettlementItemDto']
/** P2 試算 request body（對齊後端 CalculateSecondSettlementDto）。 */
export type CalculateSecondSettlementDto = ApiSchemas['CalculateSecondSettlementDto']
/** P2 試算結果單列（對齊後端 SecondSettlementItemResultDto）。 */
export type SecondSettlementItemResultDto = ApiSchemas['SecondSettlementItemResultDto']
/** P2 試算結果（對齊後端 P2ResultDto；t33/t37 為使用者面金額，不露 cell 代碼）。 */
export type P2ResultDto = ApiSchemas['P2ResultDto']
/** P2 最新 run 輸出（對齊後端 SecondSettlementRunDto，含 t33/t37 與 manual_amount=T38）。 */
export type SecondSettlementRunDto = ApiSchemas['SecondSettlementRunDto']

/** 二回目（P2）試算：每次呼叫新增一個 settlement_run（non-overwrite）。 */
export function calculateP2(
  caseId: number | string,
  body: CalculateSecondSettlementDto,
): Promise<P2ResultDto> {
  return requestData<P2ResultDto>(`${API_BASE}/cases/${caseId}/settlements/p2/calculate`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}

/** 取得案件最新 P2 run（無 run 時回 null）。 */
export function getLatestP2(caseId: number | string): Promise<SecondSettlementRunDto | null> {
  return requestData<SecondSettlementRunDto | null>(
    `${API_BASE}/cases/${caseId}/settlements/p2/latest`,
    { method: 'GET' },
  )
}

/**
 * T38 EF 實際請求金額手入力（對特定 P2 run_id）。
 * 高風險端點（spec §9.2）:需帶 2FA step-up `code`（→ `X-2FA-Code` header）。
 */
export function submitP2ManualAmount(
  runId: number,
  body: ManualAmountDto,
  stepUpCode?: string,
): Promise<ManualAmountResultDto> {
  return requestData<ManualAmountResultDto>(
    `${API_BASE}/settlements/p2/runs/${runId}/manual-amount`,
    { method: 'POST', body, headers: mutationHeaders(stepUpCode) },
  )
}

/** 二回目 発行 / 入金確認 結果（對齊後端 SecondSettlementActionResultDto）。 */
export type SecondSettlementActionResultDto = ApiSchemas['SecondSettlementActionResultDto']

/**
 * 二回目 発行（P2Calculating → P2Issued）。
 * 後端：POST /api/v1/cases/:case_id/settlements/p2/runs/:run_id/issue
 *       （v13_settlement_runs::issue_second，RBAC = marketplace.settlements.issue）。
 * 前置：該 run 已設 T38 manual-amount（否則後端拒絕）。執行中 binary 已驗 2026-06-01。
 */
export function issueP2(
  caseId: number | string,
  runId: number,
): Promise<SecondSettlementActionResultDto> {
  return requestData<SecondSettlementActionResultDto>(
    `${API_BASE}/cases/${caseId}/settlements/p2/runs/${runId}/issue`,
    { method: 'POST', headers: mutationHeaders() },
  )
}

/**
 * 二回目 入金確認（P2Issued → P2PaymentConfirmed）。
 * 後端：POST /api/v1/cases/:case_id/settlements/p2/payment-confirmation
 *       （v13_settlement_runs::confirm_second_payment，RBAC = marketplace.settlements.payment_confirm）。
 * 執行中 binary 已驗 2026-06-01。
 */
export function confirmP2Payment(
  caseId: number | string,
): Promise<SecondSettlementActionResultDto> {
  return requestData<SecondSettlementActionResultDto>(
    `${API_BASE}/cases/${caseId}/settlements/p2/payment-confirmation`,
    { method: 'POST', headers: mutationHeaders() },
  )
}

// ---- 三回目（P3）— 設計上 DISABLED（Q2 W41/W40 公式 owner sign-off 未到）-------

/**
 * 三回目（P3）試算 request body。
 * ⚠️ Q2 BLOCKER：W41/W40 公式未經業主確認,P3 為設計上 disabled。
 * 後端 v12_third_settlements generate_report 在公式未解時一律回
 * 409 V12_THIRD_SETTLEMENT_FORMULA_BLOCKED;前端**絕不自行計算 P3**。
 * 此 body 僅在「探測 endpoint 回 blocked」時使用(以證明後端守門),不送真實金額。
 */
export interface CalculateThirdSettlementDto {
  manual_amount_jpy: number
  manual_amount_reason: string
  acknowledge_formula_blockers: boolean
}

/**
 * 探測三回目 P3 calculate endpoint(預期回 409 blocked)。
 * 設計上不會成功 —— 用來向使用者證明後端對未確認公式的守門(spec §8.1)。
 */
export function calculateP3(
  caseId: number | string,
  body: CalculateThirdSettlementDto,
): Promise<unknown> {
  return requestData<unknown>(`${API_BASE}/cases/${caseId}/settlements/p3/calculate`, {
    method: 'POST',
    body,
    headers: mutationHeaders(),
  })
}
